import api from './api'
import type { PageResponse } from '@/types/comum'
import type {
  ConfiguracaoLgpd,
  EstadoSolicitacaoTitular,
  EventoSolicitacaoTitular,
  MinhaSolicitacao,
  NovaSolicitacaoPropriaRequest,
  RegistrarSolicitacaoRequest,
  SistemaChecklist,
  SolicitacaoTitular,
  TipoSolicitacaoTitular,
  TransicaoSolicitacaoRequest,
} from '@/types/privacidade'

/**
 * `originei-auth` — solicitações do titular (AN-12 §4; contrato REAL do DEV-AA, sha 62295ea).
 * `/configuracao`, `/minhas` e o `POST` raiz: qualquer autenticado (só as do próprio `sub`).
 * Listagem/registro/busca por documento: DPO ou ADMIN. Transição, responsável, checklist e
 * prazo: só DPO. Executar anonimização: só ADMIN (≠ DPO que encaminhou).
 */
const LGPD = '/api/v1/lgpd'
const BASE = `${LGPD}/solicitacoes`

const proto = (p: string) => encodeURIComponent(p)

const comoLista = <T>(r: PageResponse<T> | T[] | null | undefined): T[] =>
  !r ? [] : Array.isArray(r) ? r : (r.content ?? [])

export const solicitacoesTitularService = {
  /** Prazo informativo e flags para as telas do titular e do DPO. */
  configuracao: async (): Promise<ConfiguracaoLgpd> => api.get<ConfiguracaoLgpd>(`${LGPD}/configuracao`),

  // ---------------------------------------------------------- titular (autoatendimento)
  criarPropria: async (req: NovaSolicitacaoPropriaRequest): Promise<MinhaSolicitacao> =>
    api.post<MinhaSolicitacao>(BASE, req),

  minhas: async (page = 0, size = 50): Promise<MinhaSolicitacao[]> =>
    comoLista(await api.get<PageResponse<MinhaSolicitacao> | MinhaSolicitacao[]>(`${BASE}/minhas`, { params: { page, size } })),

  /** 409 SOLICITACAO_NAO_CANCELAVEL fora de RECEBIDA/EM_ANALISE. */
  cancelarMinha: async (protocolo: string, motivo?: string): Promise<MinhaSolicitacao> =>
    api.post<MinhaSolicitacao>(`${BASE}/minhas/${proto(protocolo)}/cancelar`, motivo ? { motivo } : {}),

  // ---------------------------------------------------------- DPO / ADMIN
  listar: async (
    filtro: { estado?: EstadoSolicitacaoTitular; tipo?: TipoSolicitacaoTitular } = {},
    page = 0,
    size = 50
  ): Promise<SolicitacaoTitular[]> => {
    const params: Record<string, string | number> = { page, size }
    if (filtro.estado) params.estado = filtro.estado
    if (filtro.tipo) params.tipo = filtro.tipo
    return comoLista(await api.get<PageResponse<SolicitacaoTitular> | SolicitacaoTitular[]>(BASE, { params }))
  },

  buscar: async (protocolo: string): Promise<SolicitacaoTitular> =>
    api.get<SolicitacaoTitular>(`${BASE}/${proto(protocolo)}`),

  eventos: async (protocolo: string): Promise<EventoSolicitacaoTitular[]> =>
    api.get<EventoSolicitacaoTitular[]>(`${BASE}/${proto(protocolo)}/eventos`),

  /** Canal externo (EMAIL|OUTRO). O CPF vai só para o HMAC. 400 CANAL_INVALIDO, DOCUMENTO_TITULAR_INVALIDO. */
  registrar: async (req: RegistrarSolicitacaoRequest): Promise<SolicitacaoTitular> =>
    api.post<SolicitacaoTitular>(`${BASE}/registrar`, req),

  /** POST (o CPF não vai em URL). 422 IDENTIFICACAO_DOCUMENTO_DESABILITADA sem pepper. */
  buscarPorDocumento: async (documento: string): Promise<SolicitacaoTitular[]> =>
    api.post<SolicitacaoTitular[]>(`${BASE}/buscar-por-documento`, { documento }),

  /** Só DPO; `nota` é o fundamento em AGUARDANDO_EXECUCAO/ATENDIDA/NEGADA. */
  transicionar: async (protocolo: string, req: TransicaoSolicitacaoRequest): Promise<SolicitacaoTitular> =>
    api.post<SolicitacaoTitular>(`${BASE}/${proto(protocolo)}/transicao`, req),

  /** Só DPO. `responsavelId` ausente = remove o responsável. */
  atribuirResponsavel: async (protocolo: string, responsavelId?: string, nota?: string): Promise<SolicitacaoTitular> =>
    api.post<SolicitacaoTitular>(`${BASE}/${proto(protocolo)}/responsavel`, { responsavelId, nota }),

  /** Só DPO. */
  marcarSistema: async (
    protocolo: string,
    sistema: SistemaChecklist,
    verificado: boolean,
    observacao?: string
  ): Promise<SolicitacaoTitular> =>
    api.post<SolicitacaoTitular>(`${BASE}/${proto(protocolo)}/sistemas-verificados`, {
      sistema,
      verificado,
      observacao,
    }),

  /** Só DPO. `null` limpa o prazo informativo. */
  definirPrazo: async (protocolo: string, prazoRespostaEm: string | null): Promise<SolicitacaoTitular> =>
    api.post<SolicitacaoTitular>(`${BASE}/${proto(protocolo)}/prazo`, { prazoRespostaEm }),

  /** Só ADMIN, diferente do DPO que pôs em AGUARDANDO_EXECUCAO (409 DUPLA_CHECAGEM_VIOLADA). */
  executarAnonimizacao: async (protocolo: string): Promise<SolicitacaoTitular> =>
    api.post<SolicitacaoTitular>(`${BASE}/${proto(protocolo)}/executar-anonimizacao`),
}

export default solicitacoesTitularService
